import { IconFont } from '@byecode/ui'
import type { SenderAccountType } from '@lighthouse/shared'
import {
    ClassNode,
    FlowControlNodeType,
    type FlowEdge,
    type FlowNode,
    getElseHandleId,
    NodeAndEdgeStatusEnum,
    type NodeTypes
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import type { EdgeProps } from '@xyflow/react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath, Position, useNodesData } from '@xyflow/react'
import { findIndex } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { EdgeLinearGradient } from '@/components/EdgeLinearGradient'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { useInteraction } from '../../hook/useInteraction'
import { NodeSelector } from '../NodeSelector'

interface CommonEdgeProps extends EdgeProps<FlowEdge> {
    onAdd?: (source: string, target: string) => void
}

interface SelectNode {
    val: NodeTypes
    senderEmailType?: SenderAccountType
}

const foreignObjectSize = 18

const SCxActionWrapper = styled.div`
    pointer-events: all;
    position: absolute;
`

const SCxEdgeButtonWrapper = styled.div`
    width: ${foreignObjectSize}px;
    height: ${foreignObjectSize}px;
    display: flex;
    justify-content: center;
    align-items: center;
`

const SCxEdgeButton = styled.div`
    width: 14px;
    height: 14px;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    border-radius: 4px;
    color: var(--color-white);
    background: transparent;
    transition: all 0.2s ease-in-out;
    background-color: var(--color-main);
    border-radius: 50%;

    &:hover {
        transform: scale(1.2);
    }
`

const SCxIcon = styled(IconFont)`
    transition: all 0.1s ease-in-out;
`

const SCxAnchorEl = styled.div``

const getEdgeStatus = (
    sourceNode: Pick<FlowNode, 'data' | 'type' | 'id'> | undefined,
    targetNode: Pick<FlowNode, 'data' | 'type' | 'id'> | undefined,
    sourceHandleId: string | undefined | null
) => {
    if (sourceNode?.data.status?.status === NodeAndEdgeStatusEnum.SUCCESS) {
        if (sourceNode?.type === ClassNode.CONDITION && sourceNode.data.nodeType === FlowControlNodeType.CONDITION) {
            const conditionSourceHandleIds = sourceNode.data.config.filterGroup.map(item => item.id)
            conditionSourceHandleIds.push(sourceNode.data.config.elseHandleId || getElseHandleId())
            const handleIndex = findIndex(id => id === sourceHandleId, conditionSourceHandleIds)
            if (handleIndex < 0) {
                return NodeAndEdgeStatusEnum.NOT_START
            }
            const output = sourceNode?.data.status?.output.flat()[handleIndex]
            if (!output?.value) {
                return NodeAndEdgeStatusEnum.NOT_START
            }
        }
        if (targetNode?.data.status?.status === NodeAndEdgeStatusEnum.SUCCESS) {
            return NodeAndEdgeStatusEnum.SUCCESS
        }
        if (targetNode?.data.status?.status === NodeAndEdgeStatusEnum.RUNNING) {
            return NodeAndEdgeStatusEnum.RUNNING
        }
        if (targetNode?.data.status?.status === NodeAndEdgeStatusEnum.FAILED) {
            return NodeAndEdgeStatusEnum.FAILED
        }
    }

    return NodeAndEdgeStatusEnum.NOT_START
}

const CommonEdge: React.FC<CommonEdgeProps> = ({
    selected,
    id,
    source,
    target,
    sourceHandleId,
    sourceX,
    sourceY,
    targetX,
    targetY,
    style,
    markerEnd,
    data
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const { type, log } = useFlowContainerContext()
    const readonly = !!log
    const [open, setOpen] = useState(false)
    const { handleNodeAdd } = useInteraction()
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX: sourceX - 9,
        sourceY,
        sourcePosition: Position.Right,
        targetX: targetX + 9,
        targetY,
        targetPosition: Position.Left,
        curvature: 0.16
    })

    const [sourceNode, targetNode] = useNodesData<FlowNode>([source, target])
    const [isHovered, setIsHovered] = useState(false)
    const disabled = useIsDisabledWithVersion() || readonly
    const handleOpen = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setOpen(true)
    }, [])

    const handleSelect = useCallback(
        ({ val, senderEmailType }: SelectNode) => {
            handleNodeAdd({
                nodeType: val,
                senderEmailType,
                prevNodeId: source,
                nextNodeId: target
            })
            setOpen(false)
        },
        [handleNodeAdd, source, target]
    )

    const status = useMemo(() => getEdgeStatus(sourceNode, targetNode, sourceHandleId), [sourceNode, targetNode, sourceHandleId])
    const gradientId = useMemo(() => `${nanoid(10)}_gradient`, [])
    const edgeLinearGradient = useMemo(() => {
        if (!readonly) {
            return null
        }

        if (status === NodeAndEdgeStatusEnum.RUNNING) {
            return (
                <EdgeLinearGradient
                    id={gradientId}
                    startColor="var(--color-green-500)"
                    stopColor="var(--color-blue-500)"
                    position={{
                        x1: sourceX,
                        y1: sourceY,
                        x2: targetX,
                        y2: targetY
                    }}
                />
            )
        }
        if (status === NodeAndEdgeStatusEnum.FAILED) {
            return (
                <EdgeLinearGradient
                    id={gradientId}
                    startColor="var(--color-green-500)"
                    stopColor="var(--color-red-500)"
                    position={{
                        x1: sourceX,
                        y1: sourceY,
                        x2: targetX,
                        y2: targetY
                    }}
                />
            )
        }
        return null
    }, [gradientId, readonly, sourceX, sourceY, status, targetX, targetY])

    const getStroke = useCallback(() => {
        if (status === NodeAndEdgeStatusEnum.RUNNING) {
            return { stroke: `url(#${gradientId})` }
        }
        if (status === NodeAndEdgeStatusEnum.FAILED) {
            return { stroke: `url(#${gradientId})` }
        }

        if (status === NodeAndEdgeStatusEnum.SUCCESS) {
            return { stroke: 'var(--color-green-500)' }
        }

        if (selected || (data?.isNodeHovering && !readonly)) {
            return { stroke: 'var(--color-main)' }
        }

        if (data?.isNodeHovering) {
            return { stroke: 'var(--color-main)' }
        }

        return { stroke: 'var(--color-gray-300)' }
    }, [data?.isNodeHovering, gradientId, readonly, selected, status])

    const edgeStyles = useMemo<React.CSSProperties>(() => {
        const defaultStyle = style ?? {}
        const stroke = getStroke()
        return { ...defaultStyle, ...stroke, strokeWidth: 2 }
    }, [getStroke, style])

    return (
        <g onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            {edgeLinearGradient}
            <BaseEdge id={id} style={edgeStyles} path={edgePath} interactionWidth={20} />
            {!disabled && (
                <EdgeLabelRenderer>
                    <SCxActionWrapper
                        style={{
                            width: foreignObjectSize,
                            height: foreignObjectSize,
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`
                        }}
                    >
                        <SCxEdgeButtonWrapper>
                            <SCxEdgeButton onClick={handleOpen} style={{ display: isHovered ? 'flex' : 'none' }}>
                                <SCxIcon size={16} type="CircleAdd" color="var(--color-main)" />
                            </SCxEdgeButton>
                        </SCxEdgeButtonWrapper>
                        <SCxAnchorEl ref={ref} />
                    </SCxActionWrapper>
                    <NodeSelector
                        target={ref.current}
                        position={Position.Right}
                        workflowType={type}
                        opened={open}
                        onSelect={handleSelect}
                        onChange={setOpen}
                    />
                </EdgeLabelRenderer>
            )}
        </g>
    )
}

export default CommonEdge
